/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Checkbox} from '@mui/material';
import {cloneDeep} from 'lodash';
import {MRT_Cell, MRT_ColumnDef} from 'material-react-table';
import {useEffect, useState} from 'react';
import {toast} from 'react-hot-toast';
import {usePostRequest} from '../../../../../../../api/helper';
import {useHasWriteAccess} from '../../../../../../../redux/ducks/AccessControl/accessControlHelpers';
import {selectDatasetsExecutionStatus, selectTriggerDoeMinMaxViolations, setDoeMinMaxViolation, setIsCoverageRequirementsSaved} from '../../../../../../../redux/ducks/CoverageRequirements/slice';
import {useAppDispatch, useAppSelector} from '../../../../../../../redux/hooks';
import ArgosFlexbox from '../../../../../../components/Containers/ArgosFlexbox';
import ArgosMenuItem from '../../../../../../components/Form/Select/ArgosMenuItem';
import ArgosSelect from '../../../../../../components/Form/Select/ArgosSelect';
import ArgosTextField from '../../../../../../components/Form/TextField/ArgosTextField';
import Loader from "../../../../../../components/Loader/Loader";
import {SCENARIO_ANALYSIS_CONSTANTS} from '../../../../../ScenarioAnalysis/constants';
import {EDatasetExecutionStatus, IN_PROGRESS_STATUSES} from '../../../constants';
import {DrivingScenarioDesignExperimentRow, drivingScenarioDetailsTable, LOADER_WRAPPER_STYLE} from './constants';
import SelectModel from './DesignOfExperimentTab/SelectModel';
import {ArgosTableSimple} from "../../../../../../components/Table/ArgosTableSimple";


interface IScenarioAnalysisGeneralInfo {
  name: string,
  version: string,
  variation: string,
  date: string,
  edgePointsOnly: boolean,
  enableRoadStatus: boolean,
  roadSuperElevation: string,
  totalNumberRuns: string
  isReadOnlyStudy?: boolean
}

interface IOptionalField {
  fieldName: string,
  value: boolean | number | string
}

interface IEnumData {
  alias: string,
  value: number
}

const DrivingScenarioDesignExperiments = (props: any) => {
  const optionalFields: IOptionalField[] = [{
    fieldName: "edgePointsOnly",
    value: false
  }, {fieldName: "roadSuperElevation", value: ''}, {fieldName: "enableRoadStatus", value: false}]

  const overrideHostAcceleration = 0;
  const fetchDrivingScenarioTableDetails = usePostRequest(drivingScenarioDetailsTable);
  const fetchDrivingScenarioVersions = usePostRequest("/HttpTriggerScenarioSidebar")
  const [scenarioData, setDrivingScenarios] = useState<any>([]);
  const [selectedScenarioAnalysisData, setSelectedScenarioAnalysisData] = useState<IScenarioAnalysisGeneralInfo | any>({});
  const [versions, setVersions] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowsData, setRowsData] = useState<any>([]);
  const [validationErrors, setValidationErrors] = useState<any>({});
  const [totalNumberRuns, setTotalNumberOfRuns] = useState<number | undefined>(undefined);
  const fetchDrivingScenarios = usePostRequest(SCENARIO_ANALYSIS_CONSTANTS.landing.endpoints.modelsTable);
  const hasWriteAccess = useHasWriteAccess()
  const [currentlyUsedList, setCurrentlyUsedList] = useState<any>(null)
  const [disableHostAccelerationProfile, setDisableHostAccelerationProfile] = useState<any>(true);
  const isReadOnlyStudy: boolean = props?.isReadOnlyStudy
  const [variation, setVariation] = useState<any>("");
  const dispatch = useAppDispatch();
  const doeMinMaxViolations = useAppSelector(selectTriggerDoeMinMaxViolations);

  const [isFocusedOnDropDown, setIsFocusedOnDropdown] = useState<boolean | null>(null)
  const [selectIndex, setSelectIndex] = useState<number>(-1)

  const selectedDatasetExecutionStatus: EDatasetExecutionStatus = useAppSelector((state) => selectDatasetsExecutionStatus(state)?.[props.selectedDatasetId])
  const shouldDisableRowOnInProgress = IN_PROGRESS_STATUSES.has(selectedDatasetExecutionStatus) || !hasWriteAccess
  const [nonVisibleDoes, setNonVisbileDoes] = useState<any>([]);
  const [numberOfRows, setNumberOfRows] = useState<number>(0);

  useEffect(() => {
    setNumberOfRows(rowsData?.length)
    let hostAccelecrationFlag: any = rowsData?.filter((doeRow: any) => doeRow?.sourceName == "host.accel_profile_flag")
    if (hostAccelecrationFlag == undefined || hostAccelecrationFlag?.length == 0) {
      setDisableHostAccelerationProfile(false)
    } else {
      let hostAccelerationContent = hostAccelecrationFlag[0];
      let selectedValues: number[] = hostAccelerationContent?.["totalSteps"]
      if (typeof (selectedValues) == "number") {
        selectedValues = [selectedValues]
      }

      if (selectedValues != undefined && !selectedValues?.includes(overrideHostAcceleration)) {
        setDisableHostAccelerationProfile(true)
      } else {
        setDisableHostAccelerationProfile(false);
      }
    }
  }, [rowsData])

  useEffect(() => {
    // if(props.selectedDatasetId != coverageRequirementsData?.id || props.isLoading) return
    setNonVisbileDoes([])
    processRowDataToHideNonVisibleDoes(props.loadedDrivingExperimentData?.variables, null, false, false)
    const variationProp = props.loadedDrivingExperimentData?.variables[0]
    if (variationProp) {
      const sourceName = variationProp['sourceName']
      if (sourceName.includes('shape')) {
        setVariation("shape")
      }
    }
    setSelectedScenarioAnalysisData(props?.loadedDrivingExperimentData?.generalInfo)
    calculateTotalNumberOfRuns(props.loadedDrivingExperimentData?.variables)
  }, [props?.loadedDrivingExperimentData])

  useEffect(() => {
    fetchDrivingScenariosData()
  }, [])

  const [isLoadingDrivingScenarios, setIsLoadingDrivingScenarios] = useState<boolean>(false)
  const fetchDrivingScenariosData = () => {
    setIsLoadingDrivingScenarios(true)
    fetchDrivingScenarios().then((data: any) => {
      setDrivingScenarios(data["table_data"])
    }).catch((e) => {
      console.log(e)
      toast.error('failed to load table data')
    }).finally(() => {
      setIsLoadingDrivingScenarios(false)
    })
  }
  const processRowDataToHideNonVisibleDoes = (loadedRowsData: any, scenarioData: any, isDataChange: boolean, isSaveOperation: boolean) => {
    let _nonVisibleDoes = loadedRowsData.filter((row: any) => row?.userVisible == false)
    let visibleDoes = loadedRowsData.filter((row: any) => row?.userVisible == true || row?.userVisible == undefined) // NOTE: This is because some models didn't have userVisible field, now we want to be safe.
    const totalNumRuns = calculateTotalNumberOfRuns(loadedRowsData)
    const nonVisibleData = _nonVisibleDoes.length > 0 ? _nonVisibleDoes : nonVisibleDoes
    setNonVisbileDoes(nonVisibleData)

    setRowsData(visibleDoes)
    if (isDataChange) {
      handleDataChange({
        "generalInfo": isSaveOperation ? {...scenarioData, totalNumberRuns: totalNumRuns} : scenarioData,
        "rowsData": cloneDeep(visibleDoes),
        "nonVisibleDoes": nonVisibleData
      })
    }
  }

  /**
   * Table definition and editing functions
   */
  function helperSetValidationErrors(newVal: string, cell: any, message: string) {
    setValidationErrors({
      ...validationErrors,
      [cell.id]: message
    })

    toast.error(message,
      {
        style: {
          marginTop: "300px"
        },
        duration: 3000
      })
  }

  const validateInput = (newVal: string, cell: any) => {
    // check if newVal has a non integer value
    if (newVal == undefined || newVal == "") {
      return false
    }

    const columnType = cell.column.id

    // what if the newVal is same as prev value? then return true
    const oldValue = cell.row.original[cell.column.id]
    if (oldValue == newVal) {
      return true
    }
    let newValnum = Number(newVal)
    switch (columnType) {
      case "min":
        // check that it is a valid float
        //convert newVal to a Number
        newValnum = Number(newVal)
        if (isNaN(newValnum)) {
          helperSetValidationErrors(newVal, cell, `Not valid value, must be a number`)
          return false
        } else if (newValnum < cell.row.original.hardMin) {
          const msg = `Min must be between ${cell.row.original.hardMin} and ${cell.row.original.hardMax}`
          helperSetValidationErrors(newVal, cell, msg)
          return false

        } else if (newValnum > cell.row.original.hardMax) {
          const msg = `Min must be between ${cell.row.original.hardMin} and ${cell.row.original.hardMax}`
          helperSetValidationErrors(newVal, cell, msg)
          return false

        } else if (newValnum > cell.row.original.max && variation != "shape") {
          const _doeMinMaxViolations = cloneDeep(doeMinMaxViolations)
          const doeName = cell.row.original.variableName;
          if (!Object.keys(_doeMinMaxViolations).includes(doeName)) {
            _doeMinMaxViolations[doeName] = `${doeName} min value is larger than its max value. Please correct this to save the data`
          }
          dispatch(setDoeMinMaxViolation(_doeMinMaxViolations))
          return true
        } else {
          const _doeMinMaxViolations = cloneDeep(doeMinMaxViolations)
          const doeName = cell.row.original.variableName;
          if (Object.keys(_doeMinMaxViolations).includes(doeName)) {
            delete _doeMinMaxViolations[doeName]
          }
          dispatch(setDoeMinMaxViolation(_doeMinMaxViolations))
          return true
        }
        break;
      case "max":
        newValnum = Number(newVal)
        if (isNaN(newValnum)) {
          helperSetValidationErrors(newVal, cell, `Not valid value, must be a number`)
          return false
        } else if (newValnum > cell.row.original.hardMax) {
          const msg = `Max must be between ${cell.row.original.hardMin} and ${cell.row.original.hardMax}`
          helperSetValidationErrors(newVal, cell, msg)
          return false
        } else if (newValnum < cell.row.original.min && variation != "shape") {
          const _doeMinMaxViolations = cloneDeep(doeMinMaxViolations)
          const doeName = cell.row.original.variableName;
          if (!Object.keys(_doeMinMaxViolations).includes(doeName)) {
            _doeMinMaxViolations[doeName] = `${doeName} min value is larger than its max value. Please correct this to save the data`
          }
          dispatch(setDoeMinMaxViolation(_doeMinMaxViolations))
          return true
        } else {
          const _doeMinMaxViolations = cloneDeep(doeMinMaxViolations)
          const doeName = cell.row.original.variableName;
          if (Object.keys(_doeMinMaxViolations).includes(doeName)) {
            delete _doeMinMaxViolations[doeName]
          }
          dispatch(setDoeMinMaxViolation(_doeMinMaxViolations))
          return true
        }
        break;
      case "default":
        newValnum = Number(newVal)
        if (isNaN(newValnum)) {
          helperSetValidationErrors(newVal, cell, `Not valid value, must be a number`)
          return false
        } else if (newValnum > cell.row.original.hardMax) {
          const msg = `Max must be between ${cell.row.original.hardMin} and ${cell.row.original.hardMax}`
          helperSetValidationErrors(newVal, cell, msg)
          return false
        } else if (newValnum < cell.row.original.min && variation != "shape") {
          helperSetValidationErrors(newVal, cell, `Max must be larger than min`)
          return false
        }
        break;
      case "totalSteps":
        const valueAsList = newVal.split(",")
        if (valueAsList.length == 1) { // not a list, just a number
          const str = valueAsList[0]
          const isFloatValid = cell.row.original.min === cell.row.original.max ? true : false;
          if (!isValidTotalSteps(str, isFloatValid)) {
            setValidationErrors({
              ...validationErrors,
              [cell.id]: `Must be ${isFloatValid ? 'a number' : 'an integer'}`
            })
            toast.error(`Must be ${isFloatValid ? 'a number' : 'an integer'}`,
              {
                style: {
                  marginTop: "300px"
                },
                duration: 3000
              })
            return false
          }
          const newValnum = Number(str)
          if (newValnum < 2) {
            setValidationErrors({
              ...validationErrors,
              [cell.id]: `Must be at least 2`
            })
            toast.error(`Must be at least 2`,
              {
                style: {
                  marginTop: "300px"
                },
                duration: 3000
              })
            return false
          }
        } else {
          //Clear previous if any error messages
          const _doeMinMaxViolations = cloneDeep(doeMinMaxViolations)
          const doeName = cell.row.original.variableName;
          if (Object.keys(_doeMinMaxViolations).includes(doeName)) {
            delete _doeMinMaxViolations[doeName]
          }
          dispatch(setDoeMinMaxViolation(_doeMinMaxViolations))

          let validationResult: boolean = true
          valueAsList.forEach((element: string) => {
            // if not integer, return false
            if (!isValidTotalSteps(element, true)) {
              setValidationErrors({
                ...validationErrors,
                [cell.id]: `Must be a number`
              })
              toast.error(`Must be a number`,
                {
                  style: {
                    marginTop: "300px"
                  },
                  duration: 3000
                })
              validationResult = false
              return false
            }
            const elementAsNumber = Number(element)
            if (elementAsNumber < cell.row.original.hardMin ||
              elementAsNumber > cell.row.original.hardMax) {
              validationResult = false
              return
            }
          })

          if (!validationResult) {
            setValidationErrors({
              ...validationErrors,
              [cell.id]: `One of the values in your list was invalid`
            })
            toast.error(`One of the values in your list was invalid`,
              {
                style: {
                  marginTop: "300px"
                },
                duration: 3000
              })
          }
          return validationResult
        }

    }

    return true
  };

  function isValidTotalSteps(num: any, isFloatValid: boolean) {
    const newValnum = Number(num)
    if (isNaN(newValnum)) return false;
    if (!isFloatValid && !Number.isInteger(newValnum)) return false;
    return true;
  }

  const getCommonEditTextFieldProps =
    (
      cell: MRT_Cell<DrivingScenarioDesignExperimentRow>,
      table: any
    ): MRT_ColumnDef<DrivingScenarioDesignExperimentRow>['muiEditTextFieldProps'] => {
      return {
        error: !!validationErrors[cell.id],
        helperText: validationErrors[cell.id],
        onKeyDown: (event: any) => {
          if (event.code === "Tab") {
            event.preventDefault();
            const {row, column} = cell;
            const allCells: any = [];
            table.getRowModel().rows.forEach((row: any) => {
              row.getAllCells().forEach((cell: any) => {
                allCells.push(cell);
              });
            });
            const flatCells = allCells;
            const currentIndex = flatCells.findIndex((c: any) => c.id === cell.id);

            // Save current cell value before moving
            const isValid = validateInput(event.target.value, cell);
            if (isValid) {
              delete validationErrors[cell.id];
              handleSaveCell(cell, event.target.value);
              setCurrentlyUsedList(null);
            }

            let nextIndex;
            if (event.shiftKey) {
              // Shift+Tab: move to previous editable cell
              const reversed = flatCells.slice(0, currentIndex).reverse();
              const foundIdx = reversed.findIndex((c: any) => c.column.columnDef.enableEditing);
              nextIndex = foundIdx !== -1 ? currentIndex - (foundIdx + 1) : currentIndex;
            } else {
              // Tab: move to next editable cell
              const sliced = flatCells.slice(currentIndex + 1);
              const foundIdx = sliced.findIndex((c: any) => c.column.columnDef.enableEditing);
              nextIndex = foundIdx !== -1 ? currentIndex + 1 + foundIdx : currentIndex;
            }

            const nextCell = flatCells[nextIndex];
            if (nextCell) {
              table.setEditingCell(nextCell);
              queueMicrotask(() => {
                const textField = table.refs.editInputRefs.current[nextCell.column.id];
                if (textField) {
                  textField.focus();
                  textField.select?.();
                }
              });
            }
          }
        },
        onBlur: (event) => {
          const isValid = validateInput(event.target.value, cell);
          if (isValid) {
            delete validationErrors[cell.id];
            handleSaveCell(cell, event.target.value)
            setCurrentlyUsedList(null)
          }
        },
      };
    }
  const checkIfSelected = (e: any) => {
    let itWasSelected = true
    e.target.value.forEach((element: any) => {
      if (overrideHostAcceleration == element) {
        itWasSelected = false
      }
    })
    return itWasSelected
  }

  const columns: MRT_ColumnDef<DrivingScenarioDesignExperimentRow>[] = [
    {
      header: "Variable Name (display)",
      accessorKey: "variableName",
      enableEditing: false,
      size: 400,
      muiEditTextFieldProps: ({cell}) => ({
        disabled: (cell.row.original.sourceName == "host.a_rate_mps2" && disableHostAccelerationProfile)
      })
    },
    {
      header: "Variable Name (source)",
      accessorKey: 'sourceName',
      size: 220,
      muiEditTextFieldProps: ({cell}) => ({
        disabled: (cell.row.original.sourceName == "host.a_rate_mps2" && disableHostAccelerationProfile)
      })
    },
    {
      header: "Units",
      accessorKey: "units",
      enableEditing: false,
      size: 160,
      muiEditTextFieldProps: ({cell}) => ({

        disabled: (cell.row.original.sourceName == "host.a_rate_mps2" && disableHostAccelerationProfile)
      })
    },
    {
      header: "Min",
      accessorFn: (row: any) => row.enumValue?.length == 0 ? row.min ?? '' : "",
      id: "min",
      enableEditing: !shouldDisableRowOnInProgress || !isReadOnlyStudy,
      size: 160,
      muiEditTextFieldProps: ({cell, table}) => ({
        ...getCommonEditTextFieldProps(cell, table),
        disabled: cell.row.original.variableType == "integer" || !hasWriteAccess || IN_PROGRESS_STATUSES.has(props.executionStatus) || cell.row.original.enumValue.length != 0
          || (typeof cell.row.original.totalSteps == "string" && cell.row.original.totalSteps.split(",").length > 1) || (cell.row.original.sourceName == "host.a_rate_mps2" && disableHostAccelerationProfile),
      }),
    },
    {
      header: "Max",
      accessorFn: (row: any) => row.enumValue?.length == 0 ? row.max ?? '' : '',
      id: "max",
      enableEditing: !shouldDisableRowOnInProgress || !isReadOnlyStudy,
      size: 160,
      muiEditTextFieldProps: ({cell, table}) => ({
        ...getCommonEditTextFieldProps(cell, table),
        disabled: cell.row.original.variableType == "integer" || !hasWriteAccess || IN_PROGRESS_STATUSES.has(props.executionStatus) || cell.row.original.enumValue.length != 0 ||
          (typeof cell.row.original.totalSteps == "string" && cell.row.original.totalSteps.split(",").length > 1) || (cell.row.original.sourceName == "host.a_rate_mps2" && disableHostAccelerationProfile),
      }),
    },
    {
      header: "Total Steps",
      accessorKey: "totalSteps",
      enableEditing: !shouldDisableRowOnInProgress || !isReadOnlyStudy,
      size: 220,
      muiEditTextFieldProps: ({cell, table}) => {
        return {
          ...getCommonEditTextFieldProps(cell, table),
          disabled:
            cell.row.original.enumValue?.length != 0
            || (cell.row.original.sourceName == "host.a_rate_mps2" && disableHostAccelerationProfile)
        }
      },
      Cell: ({cell, renderedCellValue, table}) => {
        // TODO: check improvements in code for boolean case
        if (cell.row.original.enumValue?.length != 0) {
          const isHostAcclFlag = cell.row.original.sourceName == "host.accel_profile_flag";
          let _value: any = []
          const _totalSteps = cell.row.original.totalSteps

          if (selectIndex != -1 && selectIndex == cell.row.index) {
            _value = currentlyUsedList ?? []
          } else {
            _value = _totalSteps
          }
          if (typeof (_value) == "number") {
            _value = [_value]
          }
          return <ArgosSelect
            disabled={!hasWriteAccess || isReadOnlyStudy}
            key={cell.row.index}
            multiple={true}
            style={{width: '100%'}}
            value={_value}
            onChange={(e: any) => {
              e.preventDefault()
              if (isHostAcclFlag) {
                setDisableHostAccelerationProfile(!checkIfSelected(e))
              }
              setCurrentlyUsedList(e.target.value)
            }}
            onClose={() => {
              handleSaveCell(cell, currentlyUsedList)
              setCurrentlyUsedList(null)
            }}
            onOpen={() => {
              setCurrentlyUsedList(cell.row.original.totalSteps as Array<any>)
              setSelectIndex(cell.row.index)
            }}
          >
            {cell.row.original.enumValue?.map((element: any, index: any) =>
              <ArgosMenuItem key={element.value} value={element.value}>
                {element.alias}
              </ArgosMenuItem>)}
          </ArgosSelect>
        } else {
          return <div>{renderedCellValue ?? ''}</div>
        }
      },
    },
    {
      header: "Rationale",
      accessorKey: "rationale",
      enableEditing: !isReadOnlyStudy,
      size: 220,
      muiEditTextFieldProps: ({cell, table}) => ({
        ...getCommonEditTextFieldProps(cell, table),
        disabled: shouldDisableRowOnInProgress
          || (cell.row.original.sourceName == "host.a_rate_mps2" && disableHostAccelerationProfile),
      })
    }
  ]

  const shapeColumns: MRT_ColumnDef<DrivingScenarioDesignExperimentRow>[] = [
    {
      header: "Variable Name (display)",
      accessorKey: "variableName",
      enableEditing: false,
      size: 220,
      muiEditTextFieldProps: ({cell}) => ({
        disabled: (cell.row.original.sourceName == "host.a_rate_mps2" && disableHostAccelerationProfile)
      })

    },
    {
      header: "Units",
      accessorKey: "units",
      enableEditing: false,
      size: 180,
      muiEditTextFieldProps: ({cell}) => ({

        disabled: (cell.row.original.sourceName == "host.a_rate_mps2" && disableHostAccelerationProfile)
      })
    },
    {
      header: "Variable Name (source)",
      accessorKey: 'sourceName',
      size: 220,
      muiEditTextFieldProps: ({cell}) => ({
        disabled: (cell.row.original.sourceName == "host.a_rate_mps2" && disableHostAccelerationProfile)
      })
    },
    {
      header: "Value",
      accessorFn: (row: any) => row.enumValue?.length == 0 ? row.default ?? '' : '',
      id: "default",
      enableEditing: !shouldDisableRowOnInProgress || !isReadOnlyStudy,
      size: 220,
      muiEditTextFieldProps: ({cell, table}) => ({
        ...getCommonEditTextFieldProps(cell, table),
        disabled: cell.row.original.variableType == "integer" || !hasWriteAccess || IN_PROGRESS_STATUSES.has(props.executionStatus) || cell.row.original.enumValue.length != 0
          || (typeof cell.row.original.totalSteps == "string" && cell.row.original.totalSteps.split(",").length > 1) || (cell.row.original.sourceName == "host.a_rate_mps2" && disableHostAccelerationProfile),
      }),
    },
    {
      header: "Rationale",
      accessorKey: "rationale",
      enableEditing: !isReadOnlyStudy,
      size: 220,
      muiEditTextFieldProps: ({cell, table}) => ({
        ...getCommonEditTextFieldProps(cell, table),
        disabled: shouldDisableRowOnInProgress
          || (cell.row.original.sourceName == "host.a_rate_mps2" && disableHostAccelerationProfile),
      })
    }
  ]

  /**
   * Update the table rows and columns when the model, version, or variation changes
   */
  const handleModelChange = (key: string, value: string) => {
    let newScenarioData: any = {}
    switch (key) {
      case "model":
        // Reset values when changing model
        setSelectedScenarioAnalysisData({});

        if (value.includes('Circle') || value.includes('Rectangle') || value.includes('Triangle'))
          setRowsData([]);

        setNonVisbileDoes([])

        newScenarioData = cloneDeep(selectedScenarioAnalysisData)
        newScenarioData = Object.assign(newScenarioData, {
          "name": value,
          "version": "",
          "edgePointsOnly": false,
          "enableRoadStatus": true,
          "roadSuperElevation": "0.12",
          "date": "",
          "variation": ""
        })
        break
      case "version":
        newScenarioData = cloneDeep(selectedScenarioAnalysisData)
        newScenarioData = Object.assign(newScenarioData, {
          "version": value,
          "variation": ""
        })
        break
      case "variation":
        newScenarioData = cloneDeep(selectedScenarioAnalysisData)
        newScenarioData = Object.assign(newScenarioData, {
          "variation": value,
        })
        if (value.includes('Circle') || value.includes('Rectangle') || value.includes('Triangle')) {
          setVariation("shape")
        } else {
          setVariation("model")
        }
        populateTableRows(newScenarioData, value)
        break
    }
    setSelectedScenarioAnalysisData(newScenarioData)
  }

  const populateTableRows = (newData: any, value: string) => {
    setIsLoading(true)
    fetchDrivingScenarioTableDetails({
      "model": newData.name,
      "version": newData.version,
      "variation": newData.variation,
      "variationIsId": false,
    }).then((data => {
      populateTableValues(data, newData, value)
    })).catch((e) => {
      toast.error("An error occurred while fetching the selected model's data")
    }).finally(() => {
      setIsLoading(false)
    })
  }
  const populateMin = (record: any) => {
    const data = rowsData?.filter((row: any) => row.sourceName == record.variable_name)[0]

    if (data?.min < record.min || data?.min > record.max)
      return record.min
    else if ((data?.min >= data?.hardMin && data?.min <= data?.hardMax) || (data?.min === '-' && data?.totalSteps.length && data?.totalSteps.split(",").length > 1))
      return data?.min
    else
      return record.min
  }
  const populateMax = (record: any) => {
    const data = rowsData?.filter((row: any) => row.sourceName == record.variable_name)[0]

    if (data?.max > record.max || data?.max < record.min)
      return record.max
    else if ((data?.max >= data?.hardMin && data?.max <= data?.hardMax) || (data?.max === '-' && data?.totalSteps.length && data?.totalSteps.split(",").length > 1))
      return data?.max
    else
      return record.max
  }

  const populateRationale = (varName: any) => {

    //if (rowsData){
    const rationale = rowsData?.filter((row: any) => row.sourceName == varName)[0]?.rationale;
    //}

    if (rationale != 'undefined')
      return rationale;
    else
      return "";
  }

  const populateTableValues = (data: any, scenarioData: any, value: string) => {
    let isShape: boolean = false
    if (value.includes('Circle') || value.includes('Rectangle') || value.includes('Triangle')) {
      isShape = true
    } else {
      isShape = false
    }
    const localRowsData: any = data.table_data.filter((row: any) => row["i_o"] == "input").map((element: any, index: any) => ({
      "variableName": element.display_name,
      //"min": !element?.user_visible || isShape ? element?.default : (rowsData[index]?.min == 'undefined' || rowsData[index]?.sourceName == "host.a_rate_mps2" ? element.min : rowsData[index]?.min),
      //"min": !element?.user_visible || isShape ? element?.default : element.min,
      "min": !element?.user_visible || isShape ? element?.default : populateMin(element),
      //"max": !element?.user_visible || isShape ? element?.default : (rowsData[index]?.max == 'undefined' || rowsData[index]?.sourceName == "host.a_rate_mps2" ? element.max : rowsData[index]?.max),
      //"max": !element?.user_visible || isShape ? element?.default : element.max,
      "max": !element?.user_visible || isShape ? element?.default : populateMax(element),
      //"rationale": rowsData && rowsData[index]?.rationale == 'undefined' ? "" : rowsData[index]?.rationale,
      "rationale": populateRationale(element.variable_name),
      //"rationale": "",
      "sourceName": element.variable_name,
      //"totalSteps": !element?.user_visible || isShape ? 1 : defineTotalStepsBasedOnDataType( rowsData[index]?.totalSteps == 'undefined' ? element : rowsData[index]),
      "totalSteps": !element?.user_visible || isShape ? 1 : defineTotalStepsBasedOnDataType(element),
      //"totalSteps": rowsData[index]?.totalSteps == 'undefined' ? (!element?.user_visible || isShape ? 1 : defineTotalStepsBasedOnDataType(element)) : rowsData[index]?.totalSteps,
      "scenarioVariableName": element.python_name,
      "variableType": element.data_type,
      "io": element.i_o,
      "hardMin": element.min,
      "hardMax": element.max,
      "enumValue": element.enum_data,
      "units": element.units,
      "userVisible": element?.user_visible,
      "default": element?.default
    }))

    let isDataChange: boolean = true
    let isSaveOperation: boolean = false;
    processRowDataToHideNonVisibleDoes(localRowsData, scenarioData, isDataChange, isSaveOperation)

  }
  const modifyEnumData = (enumData: IEnumData[]) => {
    enumData.forEach((element: IEnumData) => {
      element.alias = element.alias == "Use override's acceleration profile" ? "Use 'Host Acceleration Rate'" : "Use 'Host ADAS Program's Acceleration Profile'"
    })
    return enumData
  }
  const calculateTotalNumberOfRuns = (_rowsData: any) => {
    let newTotalNumberOfRuns = 1;
    _rowsData?.forEach((row: DrivingScenarioDesignExperimentRow) => {
      if (typeof row.totalSteps != "object") {
        if (typeof row.totalSteps == "number") {
          newTotalNumberOfRuns *= row.totalSteps == 0 ? 1 : row.totalSteps as unknown as number
        } else {
          const rowTotalStepsAsList = row.totalSteps.split(",")
          if (rowTotalStepsAsList.length == 1) {
            newTotalNumberOfRuns *= rowTotalStepsAsList[0] as number
          } else {
            newTotalNumberOfRuns *= rowTotalStepsAsList.length
          }
        }
      } else {
        const listLengthForCalc = (row.totalSteps === null || row.totalSteps.length == 0) ? 1 : row.totalSteps.length
        newTotalNumberOfRuns *= listLengthForCalc
      }
    })
    setTotalNumberOfRuns(newTotalNumberOfRuns)
    return newTotalNumberOfRuns
  }
  const defineTotalStepsBasedOnDataType = (element: any) => {


    const record = rowsData?.filter((row: any) => row.sourceName == element.variable_name)[0];


    if (record?.totalSteps)
      if (record?.totalSteps instanceof Array && record?.totalSteps.length != 0) {

        const enumVal = record?.enumValue;

        const filteredArray = enumVal.filter((item: any) => record?.totalSteps.includes(item.value));

        const elementArray = element.enum_data.filter((item: any) =>
          filteredArray.some((filterItem: any) => filterItem.alias === item.alias)
        );

        const valuesArray = elementArray.map((item: any) => item.value);

        return valuesArray;
      } else
        return record?.totalSteps;

    if (element.enum_data.length != 0 || element.data_type == "integer") {
      return []
    } else if (element.min == element.max) {
      return 1;
    }
    return 2;
  }
  const handleCheck = (checkValue: any) => {
    let newData = cloneDeep(selectedScenarioAnalysisData)
    newData = Object.assign(newData, {
      "enableRoadStatus": checkValue?.target.checked,
    })
    setSelectedScenarioAnalysisData(newData)
    handleDataChange({
      "generalInfo": newData,
      "rowsData": cloneDeep(rowsData),
      "nonVisibleDoes": nonVisibleDoes
    })
  }
  const handleEdgePointsOnly = (checkValue: any) => {
    if (checkValue?.target.checked) {
      toast.success("This optimization might yield a fewer results than expected")
    }
    let newData = cloneDeep(selectedScenarioAnalysisData)
    newData = Object.assign(newData, {
      "edgePointsOnly": checkValue?.target.checked
    })
    setSelectedScenarioAnalysisData(newData)
    handleDataChange({
      "generalInfo": newData,
      "rowsData": cloneDeep(rowsData),
      "nonVisibleDoes": nonVisibleDoes
    })
  }
  const handleDataChange = (data: any) => {
    if (data.rowsData != undefined && data.rowsData.length != 0) {
      const _rowsData = data.rowsData.concat(data?.nonVisibleDoes)
      optionalFields.forEach((field: IOptionalField) => {
        if (Object.keys(data.generalInfo).indexOf(field.fieldName) == -1) {
          data.generalInfo[field.fieldName] = field.value
        }
      })

      const _totalNumberOfRuns = calculateTotalNumberOfRuns(_rowsData)
      data.generalInfo["totalNumberRuns"] = _totalNumberOfRuns;
      const designExperimentData = {
        "generalInfo": data.generalInfo,
        "variables": _rowsData,
      }
      props?.handleExperimentsDataChange(designExperimentData)
    }
  }
  const handleRoadSuperElevationChange = (newValue: string) => {
    const newValueAsNumber = Number(newValue)
    if (newValueAsNumber < 0 || newValueAsNumber > 1 || isNaN(newValueAsNumber)) {
      toast.error("Invalid Value. Road Super elevation must be a value between 0 and 1 and valid number")
    } else {
      const newData: any = {...selectedScenarioAnalysisData, "roadSuperElevation": newValueAsNumber}
      setSelectedScenarioAnalysisData({...selectedScenarioAnalysisData, "roadSuperElevation": newValueAsNumber})
      handleDataChange({
        "generalInfo": newData,
        "rowsData": rowsData,
        "nonVisibleDoes": nonVisibleDoes
      })
    }

  }

  useEffect(() => {
    if (selectedScenarioAnalysisData?.version != null && selectedScenarioAnalysisData?.version?.length === 0) {
      dispatch(setIsCoverageRequirementsSaved(true))
    }
    // else {
    //   dispatch(setIsCoverageRequirementsSaved(false))
    // }
  }, [selectedScenarioAnalysisData]);
  const handleSaveCell = (cell: MRT_Cell<DrivingScenarioDesignExperimentRow>, value: any) => {
    const _rowsData = cloneDeep(rowsData)
    if (cell.column.id == "totalSteps" && cell.row.original.enumValue.length != 0) {
      _rowsData[cell.row.index][cell.column.id as keyof DrivingScenarioDesignExperimentRow] = typeof value === 'string' ? value.split(',') : value
    } else if (cell.column.id == "totalSteps" && cell.row.original.enumValue.length == 0) {
      const valuesAsList = value.split(",")
      _rowsData[cell.row.index][cell.column.id as keyof DrivingScenarioDesignExperimentRow] = value
      if (valuesAsList.length > 1) {
        _rowsData[cell.row.index].min = "-"
        _rowsData[cell.row.index].max = "-"
      }
    } else if (cell.column.id == "default" && cell.row.original.enumValue.length == 0) {
      const valuesAsList = value.split(",")
      _rowsData[cell.row.index][cell.column.id as keyof DrivingScenarioDesignExperimentRow] = value
      if (valuesAsList.length >= 1) {
        _rowsData[cell.row.index].min = value
        _rowsData[cell.row.index].max = value
      }
    } else if (cell.column.id == "rationale") {
      _rowsData[cell.row.index][cell.column.id as keyof DrivingScenarioDesignExperimentRow] = value
    } else {
      _rowsData[cell.row.index][cell.column.id as keyof DrivingScenarioDesignExperimentRow] = value
      if (_rowsData?.[cell.row.index].min == _rowsData?.[cell.row.index].max) {
        _rowsData[cell.row.index].totalSteps = 1
      } else if (rowsData[cell.row.index].totalSteps == 1) {
        _rowsData[cell.row.index].totalSteps = 2
      }
    }
    setSelectIndex(-1)
    calculateTotalNumberOfRuns(_rowsData)
    let isDataChange: boolean = true
    let isSaveOperation: boolean = true;
    processRowDataToHideNonVisibleDoes(cloneDeep(_rowsData), cloneDeep(selectedScenarioAnalysisData), isDataChange, isSaveOperation)
  }
  return (
    <>
      <Loader isLoading={props?.isLoading ?? false} wrapperStyle={LOADER_WRAPPER_STYLE}>
        <ArgosFlexbox vertical style={{marginBottom: "20px", width: "100%", overflow: "auto"}}>
          <SelectModel
            designOfExperimentData={selectedScenarioAnalysisData}
            handleChange={handleModelChange}
            disableSelect={shouldDisableRowOnInProgress || isReadOnlyStudy}
          />
          {rowsData.length != 0 &&
            <ArgosFlexbox vertical style={{width: "100%", overflow: 'auto'}}>
              <div>
                <ArgosTableSimple
                  columns={variation === "shape" ? shapeColumns : columns}
                  data={rowsData}

                  enableTopToolbar={true}
                  editDisplayMode="cell"
                  enableEditing={!isReadOnlyStudy}
                  enablePagination={false}
                  enableBottomToolbar={false}
                  initialState={{columnVisibility: {sourceName: false}}}
                  state={{isLoading: props?.isLoading}}
                  enableFilterMatchHighlighting={true}
                  muiTableBodyCellProps={({cell, column, table}) => ({
                    onClick: () => {
                      if (cell.row.original.enumValue.length == 0) {
                        table.setEditingCell(cell); //set editing cell
                        queueMicrotask(() => {
                          const textField = table.refs.editInputRefs.current[column.id];
                          if (textField) {
                            textField.focus();
                            textField.select?.();
                          }
                        });
                      }

                    },
                  })}
                />
              </div>
              <ArgosFlexbox>
                <ArgosFlexbox style={{alignItems: "center"}}>
                  <Checkbox
                    checked={selectedScenarioAnalysisData?.enableRoadStatus ?? true}
                    onChange={handleCheck}
                    disabled={shouldDisableRowOnInProgress || isReadOnlyStudy}/>
                  <span>Filter Road Super Elevation</span>
                  <ArgosTextField
                    onKeyDown={(e: any) => {
                      if (e.key === 'Enter') {
                        handleRoadSuperElevationChange(e.target.value)
                      }
                    }}
                    onBlur={(e: any) => {
                      handleRoadSuperElevationChange(e.target.value)
                    }}
                    onChange={(e) => {
                      let newdata = cloneDeep(selectedScenarioAnalysisData)
                      newdata = Object.assign(newdata, {
                        "roadSuperElevation": e.target.value
                      })
                      setSelectedScenarioAnalysisData(newdata)
                    }
                    }
                    value={selectedScenarioAnalysisData?.roadSuperElevation}
                    disabled={!selectedScenarioAnalysisData?.enableRoadStatus || isReadOnlyStudy}
                  />
                </ArgosFlexbox>
                <ArgosFlexbox style={{alignItems: "center"}}>
                  <Checkbox
                    checked={selectedScenarioAnalysisData?.edgePointsOnly ?? false}
                    onChange={handleEdgePointsOnly}
                    disabled={!selectedScenarioAnalysisData?.enableRoadStatus || shouldDisableRowOnInProgress || isReadOnlyStudy}/>
                  <span>Return Edge Points Only</span>
                </ArgosFlexbox>
                <ArgosFlexbox style={{alignItems: "center", marginLeft: 'auto', marginRight: 20}}>
                  <span>Total Number of Runs</span>
                  <ArgosTextField InputProps={{
                    readOnly: true
                  }}
                                  value={!selectedScenarioAnalysisData?.edgePointsOnly ? totalNumberRuns ?? 1 : "Up to " + String(totalNumberRuns ?? 1)}/>
                </ArgosFlexbox>
              </ArgosFlexbox>
            </ArgosFlexbox>
          }
        </ArgosFlexbox>
      </Loader>
    </>
  )
}
export {DrivingScenarioDesignExperiments};
